/**
  NOTE TO SELF:
  - check figma file -> did we get everything??
  - Figure out how to handle unavailable variants
 */

/**
 * AddonProducts component
 * Manages all addons for a specific product form
 */
class AddonProducts extends HTMLElement {
  constructor() {
    super();
    this.formId = this.dataset?.formId || null;
    this.addonState = new Map(); // Use a Map to track addon state
        
    this.selectors = {
      addonItem: '[data-addon]',
      mainProductPricesJSON: '[data-main-product-prices]',
      originalPriceLabel: '.original-price-label',
      totalPrice: '.total-price',
      addToCartButton: `buy-buttons[form="${this.formId}"] button > *`,
      mainProductQuantity: `form[id="${this.formId}"] [name="quantity"]`,
      originalPrice: 'sale-price',
      priceContainer: `.product-info__price-list__container--${this.formId}`,
      existingInputs: 'input[name^="items["][name$="[id]"]',
      existingQtyInputs: 'input[name^="items["][name$="[quantity]"]',
      checkbox: '[name="addon"]',
      quantity: '[name="quantity"]'
    }
    
    this.debounceTimer = null;
    this.init();
  }

  init() {
    // Find related form
    this.form = document.getElementById(this.formId);
    if (!this.form) return;
    
    this.addonProducts = this.querySelectorAll(this.selectors.addonItem);
    this.mainProductPrices = JSON.parse(this.querySelector(this.selectors.mainProductPricesJSON).textContent);
    this.mainProductQuantityInput = this.form.querySelector(this.selectors.mainProductQuantity);
    this.priceContainer = document.querySelector(this.selectors.priceContainer);
    
    this.setupInitialState();
    this.addEventListeners();
    
    this.renderFormInputs(); // Initial render
  }
  
  addEventListeners() {
    this.addonProducts.forEach(addon => {
      const { checkbox, quantity } = this.getAddonData(addon);

      if (checkbox) {
        checkbox.addEventListener('change', () => this.handleStateChange(addon));
      }
      
      if (quantity) {
        quantity.addEventListener('change', () => this.handleStateChange(addon));
        quantity.parentNode.addEventListener('click', () => this.handleStateChange(addon));
      }
    });
    

    if (this.mainProductQuantityInput) {
      this.mainProductQuantityInput.addEventListener('change', () => this.handleStateChange());
    }
    
    if (this.form.id && this.priceContainer) {
      this.form.id.addEventListener('change', () => this.handleStateChange());
    }
  }
  
  /**
   * Show accumulated price total of addons and main product
   */
  setTotalPrice() {
    const currVariant = this.mainProductPrices.find(price => price.id == this.form.id.value);
    const checkedAddons = [...this.addonState.values()].filter(addon => addon.isChecked);
    
    const originalPriceLabelElement = this.priceContainer.querySelector(this.selectors.originalPriceLabel);
    const totalPriceElement = this.priceContainer.querySelector(this.selectors.totalPrice);
    const originalPriceElement = this.priceContainer.querySelector(this.selectors.originalPrice);
    
    if (checkedAddons.length) {
      const totalPrice = currVariant.price + checkedAddons.reduce((acc, addon) => acc + addon.price * addon.quantity, 0);
      totalPriceElement.textContent = Shopify.formatMoney(totalPrice, window.themeVariables.settings.moneyFormat);
    }
    
    originalPriceLabelElement.hidden = !checkedAddons.length;
    totalPriceElement.hidden = !checkedAddons.length;
    originalPriceElement.classList.toggle('text-lg', !checkedAddons.length);
    this.priceContainer.classList.toggle('relevant', checkedAddons.length);
  }
  
  get totalItemsQuantity() {
    const addonQuantities = [...this.addonState.values()].reduce((acc, addon) => acc + addon.quantity, 0);
    const mainProductQuantity = parseInt(this.mainProductQuantityInput.value, 10);
    
    return addonQuantities + mainProductQuantity;
  }
  
  setAddToCartText() {
    const addToCartButton = document.querySelector(this.selectors.addToCartButton);
    if (!addToCartButton) return;
    
    setTimeout(() => {
      if (this.addonState.size) {
        addToCartButton.textContent = window.themeVariables.strings.addToCartWithAddons.replace('@@', this.totalItemsQuantity);
      } else {
        addToCartButton.textContent = window.themeVariables.strings.addToCartButton;
      }
    }, 100);
  }
  
  getAddonData(addon) {
    const checkbox = addon.querySelector(this.selectors.checkbox);
    const quantity = addon.querySelector(this.selectors.quantity);
    const price = addon.getAttribute('data-price');
    
    return { checkbox, quantity, price };
  }
  
  setState(addon) {
    const { checkbox, quantity, price } = this.getAddonData(addon);
    
    if (!checkbox) return;
    
    const variantId = Number(checkbox.value);
    const isChecked = checkbox.checked;
    const qty = quantity ? parseInt(quantity.value, 10) || 1 : 1;
    const money = price ? parseFloat(price) : 0;
    
    if (isChecked) {
      this.addonState.set(variantId, {
        id: variantId,
        quantity: qty,
        isChecked: isChecked,
        price: money
      });
    } else {
      this.addonState.delete(variantId);
    }
  }
  
  setupInitialState() {
    this.addonProducts.forEach(addon => {
      this.setState(addon);
    });
  }
  
  handleStateChange(addon) {
    if (addon) {
      this.setState(addon);
    }
    
    this.setAddToCartText();
    this.setTotalPrice();
    
    if (addon) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.renderFormInputs(), 50);
    }
  }
  
  createInput(name, value) {
    const input = document.createElement('input');
    input.setAttribute('type', 'hidden');
    input.setAttribute('name', name);
    input.setAttribute('value', value);
    return input;
  }
  
  removeInputs(inputs) {
    inputs.forEach(input => input.remove());
  }
  
  renderFormInputs() {
    if (!this.form) return;
    
    // Find and remove all existing addon inputs generated by this component
    // We'll identify them by a data attribute we'll add
    const existingAddonInputs = Array.from(this.form.querySelectorAll('input[data-addon-input]'));
    this.removeInputs(existingAddonInputs);
    
    // Convert state to array for processing
    const addonItems = Array.from(this.addonState.values());
    
    // Create new inputs for each addon in the state
    addonItems.forEach((item, index) => {
      const idInputName = `items[${index + 1}][id]`; // +1 to account for main product
      const qtyInputName = `items[${index + 1}][quantity]`;
      
      // Create new inputs with a data attribute to identify them
      const idInput = this.createInput(idInputName, item.id);
      const qtyInput = this.createInput(qtyInputName, item.quantity);
      
      // Add a data attribute to identify these as addon inputs
      idInput.setAttribute('data-addon-input', 'true');
      qtyInput.setAttribute('data-addon-input', 'true');
      
      this.form.appendChild(idInput);
      this.form.appendChild(qtyInput);
    });
  }
}
  
document.addEventListener('DOMContentLoaded', function() {
  if (!window.customElements.get('addon-products')) {
    window.customElements.define('addon-products', AddonProducts);
  }
});